/**
 * Local persistence, standing in for the database phase 1 does not have yet.
 *
 * Everything is keyed and shaped the way a document store would be, so moving
 * these records to a NoSQL backend later is a change of transport rather than a
 * change of model.
 */

import type { FriendStatus, TeamColor } from "../server/protocol.js";

const DB_NAME = "haf-saloon";

const DB_VERSION = 1;

const PROFILE_STORE = "profile";

const FRIENDS_STORE = "friends";

/** The single profile row; a future backend would key this by account id. */
export const PROFILE_KEY = "me";

export type Relationship = "friend" | "incoming" | "outgoing";

export interface StoredProfile {
    readonly id: string;
    readonly name: string;
    readonly color: TeamColor;
    /** Clubs green and diamonds blue, for players who cannot separate the suits. */
    readonly fourColorDeck: boolean;
}

export interface StoredFriend {
    readonly id: string;
    readonly name: string;
    readonly status: FriendStatus;
    readonly relationship: Relationship;
}

function request<T>(source: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        source.onsuccess = () => resolve(source.result);
        source.onerror = () => reject(source.error ?? new Error("the local store rejected a request"));
    });
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const opening = indexedDB.open(DB_NAME, DB_VERSION);

        opening.onupgradeneeded = () => {
            const db = opening.result;

            if (!db.objectStoreNames.contains(PROFILE_STORE)) {
                db.createObjectStore(PROFILE_STORE, { keyPath: "id" });
            }

            if (!db.objectStoreNames.contains(FRIENDS_STORE)) {
                db.createObjectStore(FRIENDS_STORE, { keyPath: "id" });
            }
        };

        opening.onsuccess = () => resolve(opening.result);
        opening.onerror = () => reject(opening.error ?? new Error("the local store could not be opened"));
    });
}

async function withStore<T>(
    store: string,
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => Promise<T>
): Promise<T> {
    const db = await openDatabase();

    try {
        return await work(db.transaction(store, mode).objectStore(store));
    }
    finally {
        db.close();
    }
}

export async function loadProfile(): Promise<StoredProfile | null> {
    const stored = await withStore(PROFILE_STORE, "readonly", (store) =>
        request<StoredProfile | undefined>(store.get(PROFILE_KEY)));

    return stored ?? null;
}

export async function saveProfile(profile: Omit<StoredProfile, "id">): Promise<StoredProfile> {
    const record: StoredProfile = { ...profile, id: PROFILE_KEY };
    await withStore(PROFILE_STORE, "readwrite", (store) => request(store.put(record)));

    return record;
}

export function loadFriends(): Promise<readonly StoredFriend[]> {
    return withStore(FRIENDS_STORE, "readonly", (store) => request<StoredFriend[]>(store.getAll()));
}

export function saveFriend(friend: StoredFriend): Promise<IDBValidKey> {
    return withStore(FRIENDS_STORE, "readwrite", (store) => request(store.put(friend)));
}

export function removeFriend(id: string): Promise<undefined> {
    return withStore(FRIENDS_STORE, "readwrite", (store) => request(store.delete(id)));
}
